'use client';

import * as React from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useAccountManagerMode } from '@/contexts/AccountManagerContext';
import { usePaymentStatus } from '@/hooks/use-payment-status';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function AccountManagerHeaderControls() {
  const { t } = useTranslation('common');
  const { orgRoutes, navigate: appNavigate } = useOrgNavigate();
  const { viewAsUser, toggleViewAsUser } = useAccountManagerMode();
  const { refetch: refetchPaymentStatus } = usePaymentStatus();
  const location = useLocation();
  const navigate = useNavigate();

  const handleToggleViewMode = async () => {
    const wasViewingAsUser = viewAsUser;
    toggleViewAsUser();

    if (wasViewingAsUser) {
      if (location.pathname.includes('/setup-payment')) {
        const billingPath = orgRoutes.BILLING();
        navigate(billingPath);
      }
      await refetchPaymentStatus();
    } else {
      await refetchPaymentStatus();
    }
  };

  const handleOpenPanel = () => {
    appNavigate(orgRoutes.ACCOUNT_MANAGER());
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleToggleViewMode}
              variant={viewAsUser ? 'destructive' : 'ghost'}
              size="icon"
              className="h-9 w-9 rounded-lg"
            >
              {viewAsUser ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
              <span className="sr-only">
                {viewAsUser ? t('accountManager.viewingAsUser') : t('accountManager.accountManagerMode')}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="font-medium">
              {viewAsUser ? t('accountManager.viewingAsUser') : t('accountManager.accountManager')}
            </p>
            <p className="text-xs text-muted-foreground">
              {viewAsUser
                ? t('accountManager.clickToDisable')
                : t('accountManager.clickToViewAsUser')}
            </p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleOpenPanel}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-lg"
            >
              <Shield className="h-4 w-4" />
              <span className="sr-only">{t('accountManager.accountManager')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{t('accountManager.accountManagerPanel')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
